"use client";

import { useId, useState } from "react";
import { Cpu, Server } from "lucide-react";
import {
  DISPLAY,
  EDITORIAL_DARK_TOKENS,
  Grain,
  GROUND,
  LABEL_CLS,
  SURFACE_DARK,
} from "@/components/editorial";
import { EuFlag, UkFlag, UsFlag } from "./drawing-variant-regions";

const REGIONS = [
  { id: "uk", name: "United Kingdom", status: "In development", Flag: UkFlag },
  { id: "eu", name: "European Union", status: "Available today", Flag: EuFlag },
  { id: "us", name: "United States", status: "Available today", Flag: UsFlag },
] as const;

type RegionId = (typeof REGIONS)[number]["id"];

function PrivateStack() {
  return (
    <figure className="m-0 min-w-0" aria-label="Proposed UK infrastructure: server and storage, your Twin, and a private model together in one UK boundary">
      <div className="flex items-center justify-between gap-4 border-b border-[var(--rule)] pb-5">
        <span className={`${LABEL_CLS} text-[var(--theme-accent)]`}>One UK boundary</span>
        <svg viewBox="0 0 100 66.67" className="h-7 w-[2.625rem]" aria-hidden="true">
          <UkFlag x={0} y={0} width={100} height={66.67} />
        </svg>
      </div>
      <div className="relative grid grid-cols-3 items-start py-9 sm:py-12">
        <div aria-hidden="true" className="absolute inset-x-[16.667%] top-[3.375rem] border-t border-[color-mix(in_srgb,var(--theme-ground)_28%,transparent)] sm:top-[4.125rem]" />
        {[
          { label: "Server + storage", Icon: Server },
          { label: "Your Twin", Icon: null },
          { label: "Private model", Icon: Cpu },
        ].map(({ label, Icon }) => (
          <div key={label} className="relative flex min-w-0 flex-col items-center gap-5 text-center">
            <span className="flex size-11 items-center justify-center text-[var(--theme-accent)]" style={{ backgroundColor: SURFACE_DARK }}>
              {Icon ? <Icon size={30} strokeWidth={1.25} aria-hidden="true" /> : (
                <svg viewBox="0 0 44 44" className="size-10" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
                  {[18, 14, 10, 6].map((radius) => <circle key={radius} cx="22" cy="22" r={radius} />)}
                </svg>
              )}
            </span>
            <span className="text-[0.78rem] leading-[1.45] sm:text-[0.9rem]">{label}</span>
          </div>
        ))}
      </div>
      <figcaption className="border-t border-[var(--rule)] pt-4 text-center text-[0.78rem] text-[color-mix(in_srgb,var(--theme-ground)_65%,transparent)]">
        Server, storage and model processing together.
      </figcaption>
    </figure>
  );
}

export function HostingInfrastructure() {
  const [region, setRegion] = useState<RegionId>("eu");
  const groupName = useId();

  return (
    <>
      <fieldset className="m-0 min-w-0 border-0 p-0">
        <legend className="sr-only">Explore hosting regions</legend>
        <div className="grid gap-3 sm:grid-cols-3 sm:gap-5">
          {REGIONS.map(({ id, name, status, Flag }) => {
            const active = id === region;
            return (
              <label key={id} className={`relative flex min-h-40 cursor-pointer flex-col items-center justify-center gap-3 border border-[var(--rule)] px-5 py-7 text-center has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-4 has-[:focus-visible]:outline-[var(--theme-accent-text)] ${active ? "bg-[var(--theme-surface-dark)] text-[var(--theme-ground)]" : "bg-[var(--theme-ground)] text-[var(--theme-ink)]"}`}>
                <input type="radio" name={groupName} value={id} checked={active} onChange={() => setRegion(id)} className="sr-only" />
                <svg viewBox="0 0 100 66.67" className="h-9 w-[3.375rem]" aria-hidden="true">
                  <Flag x={0} y={0} width={100} height={66.67} />
                </svg>
                <span style={DISPLAY} className="text-[1.15rem] font-semibold tracking-[-0.02em]">{name}</span>
                <span className="text-[0.75rem] opacity-70">{status}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
      <ul className="m-0 flex list-none flex-wrap items-center justify-center gap-x-6 gap-y-2 border-b border-dotted border-[var(--rule)] px-3 py-6 text-[0.85rem] sm:gap-x-10">
        {["Dedicated server", "Read-only connections", "Audit trail"].map((detail) => (
          <li key={detail} className="flex items-center gap-2.5"><span aria-hidden="true" className="size-1 rounded-full bg-[var(--theme-accent-text)]" />{detail}</li>
        ))}
      </ul>
      <p className="mx-auto mb-8 mt-4 max-w-[72ch] text-center text-[0.82rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ink)_65%,transparent)]">
        The region is where your server runs. Model processing can take place elsewhere, depending on the provider you use.
      </p>
      <div id="uk" className="relative scroll-mt-24 overflow-hidden" style={{ backgroundColor: SURFACE_DARK, color: GROUND, ...EDITORIAL_DARK_TOKENS }}>
        <Grain opacity={0.5} />
        <div className="relative z-10 grid items-center gap-10 px-6 py-10 sm:px-10 sm:py-12 lg:grid-cols-2 lg:gap-12">
          <div>
            <p className={`${LABEL_CLS} text-[var(--theme-accent)]`}>In development · Project Venus</p>
            <h3 style={DISPLAY} className="mt-5 max-w-[18ch] text-[1.9rem] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--theme-ground)] sm:text-[2.2rem]">Building a fully UK-hosted Twin.</h3>
            <p className="mt-4 max-w-[43ch] text-[0.95rem] leading-[1.65] text-[color-mix(in_srgb,var(--theme-ground)_82%,transparent)]">
              We are working to bring your server, storage and private model together in the UK. Not yet a standard deployment.
            </p>
            <a href="/research#uk" className="mt-6 inline-flex items-center gap-2 text-[0.85rem] font-medium text-[var(--theme-ground)] underline decoration-[color-mix(in_srgb,var(--theme-ground)_35%,transparent)] underline-offset-4 hover:decoration-current">Read the research <span aria-hidden="true">→</span></a>
          </div>
          <PrivateStack />
        </div>
      </div>
    </>
  );
}
